// Public legal pages — Privacy Policy + Terms of Service.
//
// Affiliate/partner networks (FlexOffers, CJ, Impact, issuer programs) reject
// sites without a real privacy policy and terms. These are honest, plain
// descriptions of how the Funding Hub actually behaves — not aspirational
// boilerplate. Reviewed content should stay in sync with what the platform
// does (encryption at rest, tenant isolation, audit logging, no data sale).
import { Link } from "react-router-dom";
import { applySeo } from "../lib/seo";

function LegalShell({ title, updated, children }: { title: string; updated: string; children: React.ReactNode }) {
  return (
    <div className="landing">
      <header className="landing-nav">
        <div className="landing-shell landing-nav-inner">
          <Link to="/" className="landing-logo" style={{ textDecoration: "none" }}>
            ALIEF <em>Funding Hub</em>
          </Link>
          <Link className="primary-button" to="/login">Sign in</Link>
        </div>
      </header>
      <section className="landing-section">
        <div className="landing-shell landing-legal-page">
          <h1 className="landing-h2" style={{ textAlign: "left" }}>{title}</h1>
          <p className="muted small">Last updated: {updated}</p>
          {children}
        </div>
      </section>
      <footer className="landing-footer">
        <div className="landing-shell">
          <p className="landing-legal">
            <Link to="/">Home</Link> · <Link to="/contact">Contact</Link> · <Link to="/privacy">Privacy Policy</Link> · <Link to="/terms">Terms of Service</Link> · <Link to="/disclosure">Advertiser Disclosure</Link> ·{" "}
            Alief Financial Services · <a href="mailto:support@alieffundinghub.com">support@alieffundinghub.com</a>
          </p>
        </div>
      </footer>
    </div>
  );
}

export function PrivacyPage() {
  applySeo({
    title: "Privacy Policy | Alief Funding Hub",
    description: "How the Alief Funding Hub collects, uses, and protects company and client data. We do not sell personal information.",
    path: "/privacy",
  });
  return (
    <LegalShell title="Privacy Policy" updated="August 9, 2026">
      <h2>Who we are</h2>
      <p>
        The Alief Funding Hub ("the Platform") is operated by Alief Financial Services
        ("we," "us"). The Platform is workflow software used by funding-services companies ("Companies")
        to manage their own clients' funding files. Contact: support@alieffundinghub.com.
      </p>
      <h2>What we collect</h2>
      <p>
        Companies and their staff provide account information (name, email, role). Companies enter or
        invite their clients to submit client information relevant to funding work: contact details,
        business information, financial documents, and — where a client provides it — credit-report data
        and related credentials. Clients submit this information to the Company they work with; the
        Platform stores it on that Company's behalf.
      </p>
      <h2>How we use it</h2>
      <ul>
        <li>To operate the Platform for the Company that owns the data: analysis, lender matching, document storage, portal access, and communications the Company initiates.</li>
        <li>To secure the service: authentication, rate limiting, and audit logging.</li>
        <li>We do <strong>not</strong> sell personal information, and we do not share client data across Companies. Each Company's data is isolated to its own tenant.</li>
      </ul>
      <h2>How it's protected</h2>
      <ul>
        <li>Sensitive fields (such as SSNs and stored credentials) are encrypted at rest.</li>
        <li>Access is role-based within each Company; reveals of sensitive fields are audit-logged.</li>
        <li>Documents are stored in private storage, never on public URLs.</li>
        <li>All traffic is served over HTTPS.</li>
      </ul>
      <h2>Service providers</h2>
      <p>
        We use infrastructure providers to run the Platform (cloud hosting, database, email delivery).
        They process data only to provide their service to us.
      </p>
      <h2>Retention & deletion</h2>
      <p>
        Data is retained while the owning Company maintains its account. A Company may request deletion of
        a client file or its whole tenant by contacting us; clients should direct requests to the Company
        they work with, which controls their file.
      </p>
      <h2>Your choices</h2>
      <p>
        Email us at support@alieffundinghub.com for any privacy question or request, including access and
        deletion. If you are a client of a Company using the Platform, we will coordinate with that
        Company to honor your request.
      </p>
    </LegalShell>
  );
}

export function TermsPage() {
  applySeo({
    title: "Terms of Service | Alief Funding Hub",
    description: "Terms for using the Alief Funding Hub platform. Workflow software for funding professionals — not a lender and not a credit-repair service.",
    path: "/terms",
  });
  return (
    <LegalShell title="Terms of Service" updated="August 9, 2026">
      <h2>The service</h2>
      <p>
        The Alief Funding Hub is business software for funding-services companies: client intake,
        credit-report analysis, lender matching, document handling, contracts, and client portals. It is
        provided by Alief Financial Services.
      </p>
      <h2>What the Platform is not</h2>
      <ul>
        <li>The Platform is <strong>not a lender</strong> and does not make credit decisions. All approvals, amounts, rates, and terms are determined solely by the lenders a client applies with.</li>
        <li>No approval, funding amount, timeline, or credit-score outcome is ever guaranteed by the Platform or by any analysis it produces.</li>
        <li>The Platform is <strong>not a credit-repair service</strong> and its outputs are not legal, financial, or credit advice.</li>
      </ul>
      <h2>Accounts & acceptable use</h2>
      <ul>
        <li>Companies are responsible for their staff accounts and for having proper authorization from their clients for any data they enter, upload, or access — including client-supplied credentials and credit reports.</li>
        <li>You may not use the Platform for unlawful purposes, to misrepresent offers, or to submit information about a person without their authorization.</li>
        <li>We may suspend accounts that abuse the service or create risk for other tenants.</li>
      </ul>
      <h2>Data</h2>
      <p>
        As between us and a Company, the Company owns its client data. Our use of data is described in the{" "}
        <Link to="/privacy">Privacy Policy</Link>.
      </p>
      <h2>Third-party offers & compensation disclosure</h2>
      <p>
        Where the Platform or our sites reference financial products offered by third parties, the
        decision to offer or approve any product belongs to that third party. If we receive compensation
        from a partner in connection with a product, that relationship is disclosed where the offer
        appears.
      </p>
      <h2>Disclaimers & limitation of liability</h2>
      <p>
        The Platform is provided "as is." To the maximum extent permitted by law, Alief Financial Services disclaims implied warranties and is not liable for indirect or consequential
        damages, or for decisions made by lenders, Companies, or clients based on Platform outputs.
      </p>
      <h2>Changes & contact</h2>
      <p>
        We may update these terms; continued use after an update is acceptance. Questions:
        support@alieffundinghub.com.
      </p>
    </LegalShell>
  );
}

/* ────────────────────────────────────────────────────────────────────────
   Advertiser / affiliate disclosure.

   Required by the FTC (16 CFR Part 255) and by every affiliate network we
   publish through: the relationship has to be disclosed clearly, close to
   the recommendation, and in plain language. This page is the full version;
   a one-line disclosure sits at the top of the blog and every article, which
   is where the offers actually appear.
   ──────────────────────────────────────────────────────────────────────── */
export function DisclosurePage() {
  applySeo({
    title: "Advertiser Disclosure | Alief Funding Hub",
    description:
      "How Alief Funding Hub makes money: some lender and credit card links are affiliate links that pay us a commission. It never changes what we recommend or what you pay.",
    path: "/disclosure",
  });
  return (
    <LegalShell title="Advertiser Disclosure" updated="August 13, 2026">
      <p>
        <strong>Short version: some of the links on this site pay us.</strong> That never changes
        what we recommend, and it never costs you anything extra.
      </p>

      <h2>How we make money</h2>
      <p>
        Alief Funding Hub publishes funding and credit education, and we also sell software to
        funding companies. Some of the lender, credit card, and financial product links in our
        articles are affiliate links. If you click one and open an account or get funded, the
        company may pay us a commission or referral fee.
      </p>
      <p>
        We may also be paid by credit monitoring services when a reader signs up through our link,
        and we earn fees directly from clients who hire us for funding or credit services. Those
        arrangements are separate from anything you pay a lender.
      </p>

      <h2>What it does not change</h2>
      <ul>
        <li><strong>You never pay more.</strong> Rates, terms, and fees are set by the lender or issuer, not by us, and are identical whether you use our link or go direct.</li>
        <li><strong>It does not decide what we recommend.</strong> Our recommendations come from the credit profile in front of us — the score, the file, the state, and what that lender actually approves. A product does not rank higher because it pays us, and products that pay us nothing appear throughout this site.</li>
        <li><strong>We tell you the downsides.</strong> Where a product has a catch — a business card that reports to your personal credit, an origination fee, a hard inquiry — we say so, including when we would earn a commission on it.</li>
      </ul>

      <h2>What we are not</h2>
      <p>
        We are <strong>not a lender</strong> and we do not make credit decisions. Every approval,
        amount, rate, and term is decided solely by the lender or issuer you apply with. Nothing on
        this site is a guarantee of approval, a promise about your credit score, or an offer of
        credit. Nothing here is legal, tax, or financial advice for your specific situation.
      </p>

      <h2>Editorial independence</h2>
      <p>
        Advertisers and affiliate partners do not review, approve, or edit our articles before they
        are published, and they have no say in how their products are described.
      </p>

      <p>
        Questions about any of this — including whether a specific link is an affiliate link — email{" "}
        <a href="mailto:support@alieffundinghub.com">support@alieffundinghub.com</a> and we will tell you
        straight.
      </p>
    </LegalShell>
  );
}
